import { DateObject, useFormatAmount, useIntl } from '@capsicum/common';
import { useMe } from '@capsicum/socios-core';
import { useCallback, useMemo, useRef, useState } from 'react';

import { type CollectibleWithImage } from '../../models';
import { AuctionStatus } from '../../models/auctions';

export const useAuctionCard = (auction: CollectibleWithImage) => {
	const { $t } = useIntl();
	const { me } = useMe();
	const { formatAmount } = useFormatAmount();

	const { auctionEndDate, currentStatus, setCurrentStatus } = useAuctionTimer(auction);

	const isBall = useMemo(() => auction?.collectible?.type === 'goal_ball', [auction?.collectible?.type]);

	const hasBids = useMemo(() => !!auction.currentBid, [auction.currentBid]);

	const texts = useMemo(
		() => ({
			closed: $t('rewards.auctions.closed'),
			currentBid: $t('rewards.auctions.status.currentBid'),
			goalBy: $t('rewards.auctions.goalBy'),
			noBids: $t('rewards.auctions.noBids'),
			own: $t('rewards.auctions.status.yourBid'),
			winning: $t('rewards.auctions.status.winning'),
			won: $t('rewards.auctions.status.youWonTheBid')
		}),
		[$t]
	);

	const getAuctionStatus = useCallback(() => {
		const lastBidderId = auction.currentBid?.participant?.chilizUserId;

		const isFinished = auction.status === AuctionStatus.FINISHED.toString();
		const isOwnBid = me.externalUserId === lastBidderId;

		if (isFinished) {
			if (isOwnBid) {
				return AuctionStatus.WON;
			}

			return AuctionStatus.WINNING;
		}

		if (isOwnBid) {
			return AuctionStatus.OWN;
		}

		return AuctionStatus.ACTIVE;
	}, [me, auction]);

	const bidStatusLabel = useCallback(() => {
		switch (getAuctionStatus()) {
			case AuctionStatus.WINNING:
				return texts.winning;
			case AuctionStatus.WON:
				return texts.won;
			case AuctionStatus.OWN:
				return texts.own;
			default:
				return texts.currentBid;
		}
	}, [texts, getAuctionStatus]);

	const getAuctionImage = useMemo(() => {
		return auction?.collectible?.providerImages?.[0] || '';
	}, [auction]);

	const sortedTags = useMemo(() => {
		return auction && auction.collectible?.tags?.sort((a, b) => b.displayOrder! - a.displayOrder!);
	}, [auction]);

	const price = `${
		formatAmount(auction.currentBid?.altCurrency?.bidAmount) || formatAmount(auction.altCurrency?.minBidAmount)
	} ${auction.altCurrency?.currency?.shortCode}`;
	const priceChz = `${
		formatAmount(auction.currentBid?.mainCurrency?.bidAmount, {
			maximumFractionDigits: 2
		}) ||
		formatAmount(auction.mainCurrency?.minBidAmount, {
			maximumFractionDigits: 2
		})
	} ${auction.mainCurrency?.currency?.shortCode}`;

	return {
		auctionEndDate,
		bidStatusLabel,
		currentStatus,
		getAuctionImage,
		getAuctionStatus,
		hasBids,
		isBall,
		price,
		priceChz,
		setCurrentStatus,
		sortedTags,
		texts
	};
};

export const useAuctionTimer = (auction: CollectibleWithImage | undefined) => {
	const [currentStatus, setCurrentStatus] = useState<string>('');
	const auctionEndDate = new DateObject(auction?.endAt || '');

	//#region for usage inside the flashlist
	// https://shopify.github.io/flash-list/docs/recycling
	const lastAuctionId = useRef(auction?.id);

	if (auction?.id !== lastAuctionId.current) {
		lastAuctionId.current = auction?.id;
		setCurrentStatus('');
	}
	//#endregion

	return {
		auctionEndDate,
		currentStatus,
		setCurrentStatus
	};
};
